// @flow
/** @jsx h */
import { h, Fragment } from 'preact';
import {
  useErrorModalContext,
  useErrorModalContextDispatch,
} from '../../storeProviders/errorModalStore';
import { styled } from 'goober';
import errorIcon from '../../assets/salesApp/error-icon.svg';
import Button from 'preact-material-components/Button';
import 'preact-material-components/Button/style.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useSalesDispatchContext } from '../../storeProviders/salesAppStore';
import Match from 'preact-router/match';
import { route } from 'preact-router';

const Container = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  .errorIcon {
    margin-top: 64px;
  }
  .errorTitle {
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
    color: #9e170e;
    text-align: center;
    max-width: 345px;
    margin-top: 42px;
  }
  .errorDesc {
    font-weight: 500;
    font-size: 16px;
    line-height: 18px;
    color: #455a64;
    max-width: 345px;
    margin: 16px 0px 48px;
    text-align: center;
  }
  .exitBtn {
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    text-align: center;
    letter-spacing: 0.1px;
    text-decoration-line: underline;
    color: #1a73e8;
    margin-bottom: 30px;
    cursor: pointer;
  }
  .okBtn {
    width: 318px;
    height: 50px;
    margin: 0 auto 16px;
    background-color: #007e3f;
    border-radius: 8px;
    border-color: transparent;
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
    text-align: center;
    letter-spacing: 0.1px;
    color: #ffffff;
    text-transform: capitalize;
  }
`;

export const ErrorContentUi = ({
  icon,
  title,
  message,
  handleCloseModal,
  handleCancelClick,
  retryButtonText,
  closeButtonText,
  isRetryAllowed,
  hideCloseButton,
}) => {
  return (
    <Container>
      <div className='errorIcon'>
        <svg
          width='60'
          height='60'
          viewBox='0 0 60 60'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'>
          <circle cx='30' cy='30' r='28' stroke='#9E170E' stroke-width='4' />
          <path
            d='M32.4865 16.8182L32.0646 35.304H27.9482L27.5391 16.8182H32.4865ZM30.0064 43.2812C29.2308 43.2812 28.5661 43.0085 28.0121 42.4631C27.4666 41.9176 27.1939 41.2528 27.1939 40.4688C27.1939 39.7017 27.4666 39.0455 28.0121 38.5C28.5661 37.9545 29.2308 37.6818 30.0064 37.6818C30.7649 37.6818 31.4212 37.9545 31.9751 38.5C32.5376 39.0455 32.8189 39.7017 32.8189 40.4688C32.8189 40.9886 32.6868 41.4616 32.4226 41.8878C32.1669 42.3139 31.826 42.6548 31.3999 42.9105C30.9822 43.1577 30.5178 43.2812 30.0064 43.2812Z'
            fill='#9E170E'
          />
        </svg>
      </div>
      <div className='errorTitle'>{title}</div>
      <div className='errorDesc'>{message}</div>
      {isRetryAllowed && (
        <Button
          className='okBtn'
          onClick={() => {
            handleCloseModal();
          }}>
          {retryButtonText ? retryButtonText : 'Try Again'}
        </Button>
      )}
      {!hideCloseButton && (
        <div className='exitBtn' onClick={handleCancelClick}>
          {closeButtonText ? closeButtonText : 'Exit'}
        </div>
      )}
    </Container>
  );
};

const ErrorContent = () => {
  const errorModalState = useErrorModalContext();
  const {
    isErrorModalOpen,
    title,
    errorCode,
    message,
    handleClose,
    closeButtonText,
    isRetryAllowed,
    hideCloseButton,
    onRetryClick,
    retryButtonText,
    isRetryPrimary,
    isClosePrimary,
  } = errorModalState || {};
  const errorModalDispatch = useErrorModalContextDispatch();
  const [open, setOpen] = useState(false);
  const pathRef = useRef();
  const salesDispatch = useSalesDispatchContext();

  useEffect(() => {
    if (title) {
      setOpen(true);
    } else {
      setOpen(false);
      salesDispatch({
        type: 'STORE_STATE',
        payload: {
          bottomSheetConfig: {
            showOverlay: false,
            moduleName: null,
          },
        },
      });
    }
  }, [title]);

  const handleCloseModal = () => {
    setOpen(false);
    errorModalDispatch({
      type: 'MODAL/CLOSE',
    });
    salesDispatch({
      type: 'STORE_STATE',
      payload: {
        bottomSheetConfig: {
          showOverlay: false,
          moduleName: null,
        },
      },
    });
    if (onRetryClick) {
      onRetryClick();
    }
  };

  const handleCancelClick = () => {
    errorModalDispatch({
      type: 'MODAL/CLOSE',
    });
    setOpen(false);
    salesDispatch({
      type: 'STORE_STATE',
      payload: {
        bottomSheetConfig: {
          showOverlay: false,
          moduleName: null,
        },
      },
    });
    handleClose();
  };

  useEffect(() => {
    const handleBackClick = () => {
      if (open) {
        if (pathRef.current) {
          route(pathRef.current);
        }
        if (isRetryAllowed) {
          handleCloseModal();
        } else {
          handleCancelClick();
        }
      }
    };

    if (open) {
      const disableBackButton = () => {
        route(pathRef.current);
        window.addEventListener('popstate', handleBackClick);
      };

      disableBackButton();
    }

    return () => {
      window.removeEventListener('popstate', handleBackClick);
    };
  });

  return (
    <Fragment>
      <Match>
        {({ path }) => {
          pathRef.current = path;
        }}
      </Match>
      <ErrorContentUi
        icon={errorIcon}
        title={title}
        message={message}
        handleCloseModal={handleCloseModal}
        handleCancelClick={handleCancelClick}
        retryButtonText={retryButtonText}
        closeButtonText={closeButtonText}
        hideCloseButton={hideCloseButton}
        isRetryAllowed={isRetryAllowed}
      />
    </Fragment>
  );
};

export default ErrorContent;



// WEBPACK FOOTER //
// ./components/salesApp/ErrorContent.js